import React from "react";
import { makeStyles } from "@material-ui/core/styles";

// Material
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";

// Assets
import mainImage from "../../assets/backgrounds/getConnectedPage/implementationImage.png";
import implementationBg from "../../assets/backgrounds/getConnectedPage/implementationBg.png";
import sepBg from "../../assets/backgrounds/getConnectedPage/sepBg.png";

// Config
import { urls } from "../../config/urls";

// Components
import Section from "../../components/Section/Section";
import Text, { types } from "../../components/Text/Text";

// Icons
import complimentary from "../../assets/backgrounds/getConnectedPage/complimentary.svg";
import easySetup from "../../assets/backgrounds/getConnectedPage/easySetup.svg";
import implementationBlueprint from "../../assets/backgrounds/getConnectedPage/implementationBlueprint.svg";
import paperworkComplete from "../../assets/backgrounds/getConnectedPage/paperworkComplete.svg";
import transferEvidence from "../../assets/backgrounds/getConnectedPage/transferEvidence.svg";
import LinkScroll from "../../components/LinkScroll/LinkScroll";

const useStyles = makeStyles(theme => ({
  root: {
    padding: "240px 8vw",
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: 140,
    backgroundImage: `url(${implementationBg})`,
    backgroundPosition: "center center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    [theme.breakpoints.down("md")]: {
      gap: "5vw",
      paddingTop: "180px"
    },
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column-reverse",
      gap: 0,
      padding: "48px 6vw"
    },
    [theme.breakpoints.down(512)]: {
      padding: "48px 12px"
    }
  },
  textWrapper: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    gap: theme.spacing(4),
    [theme.breakpoints.down("md")]: {
      gap: theme.spacing(3)
    },
    [theme.breakpoints.down("sm")]: {
      gap: 0
    }
  },
  title: {
    color: "#171817",
    maxWidth: "20ch",
    margin: "0",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      maxWidth: "100%",
      lineHeight: 1.5,
      marginBottom: theme.spacing(1)
    }
  },
  imageBg: {
    width: "234px",
    [theme.breakpoints.down("md")]: {
      width: "180px"
    },
    [theme.breakpoints.down(1440)]: {
      width: "160px"
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      maxWidth: "97px",
      marginBottom: theme.spacing(3)
    }
  },
  itemWrapper: {
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      marginBottom: theme.spacing(2),
      alignItems: "flex-start"
    }
  },
  icon: {
    [theme.breakpoints.down("sm")]: {
      width: "24px"
    }
  },
  titleDescription: {
    fontSize: "28px",
    fontFamily: ["Montserrat", "sans-serif"].join(","),
    lineHeight: 1.2,
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("md")]: {
      fontSize: "24px"
    },
    [theme.breakpoints.down(1440)]: {
      fontSize: "18px"
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "16px"
    }
  },
  spanCard: {
    fontWeight: 700
  },
  button: {
    fontFamily: "Montserrat Black",
    textTransform: "none",
    fontSize: "24px",
    padding: "28px 175px",
    borderRadius: "10px",
    boxShadow: "0px 3px 66px #00000029",
    color: "#fff",
    lineHeight: 1,
    [theme.breakpoints.down("md")]: {
      fontSize: "18px",
      padding: "24px 90px"
    },
    [theme.breakpoints.down(1440)]: {
      // fontSize: "14px",
      // marginBottom: theme.spacing(4),
      // padding: "24px 70px"
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "14px",
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(4),
      padding: "24px 0",
      width: "100%",
      height: "70px"
    }
  },
  image: {
    // transform: " translateY(8%)",
    width: "674px",
    [theme.breakpoints.down("md")]: {
      width: "30vw"
    },
    [theme.breakpoints.down(1440)]: {
      width: "30vw"
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      maxWidth: "500px"
    }
  }
}));

const GetConnectedImplementation = () => {
  const classes = useStyles();

  return (
    <Section className={classes.root}>
      <div className={classes.textWrapper}>
        <Text type={types.sectionTitle} className={classes.title}>
          Implementation Is Straightforward
        </Text>
        <img src={sepBg} className={classes.imageBg} alt="SepBg" />
        <div className={classes.itemWrapper}>
          <img
            className={classes.icon}
            src={implementationBlueprint}
            alt="Implementation blueprint available"
          />
          <Typography className={classes.titleDescription}>
            <span className={classes.spanCard}>
              Implementation blueprint available
            </span>{" "}
            — and support and training is included.
          </Typography>
        </div>
        <div className={classes.itemWrapper}>
          <img
            className={classes.icon}
            src={paperworkComplete}
            alt="Paperwork complete"
          />
          <Typography className={classes.titleDescription}>
            <span className={classes.spanCard}>Paperwork complete:</span> Cloud
            Security Assessments and DPIAs are already in place.
          </Typography>
        </div>
        <div className={classes.itemWrapper}>
          <img className={classes.icon} src={easySetup} alt="Easy IT setup" />
          <Typography className={classes.titleDescription}>
            <span className={classes.spanCard}>Easy IT setup:</span> Whitelist
            web addresses, enable SSO via Microsoft Entra or SailPoint — done.
          </Typography>
        </div>
        <div className={classes.itemWrapper}>
          <img
            className={classes.icon}
            src={complimentary}
            alt="Complimentary"
          />
          <Typography className={classes.titleDescription}>
            <span className={classes.spanCard}>
              Complimentary to your DAMS/DEMS:
            </span>{" "}
            No system changes — just better evidence in, better outcomes out.{" "}
          </Typography>
        </div>
        <div className={classes.itemWrapper}>
          <img
            className={classes.icon}
            src={transferEvidence}
            alt="Transfer evidence"
          />
          <Typography className={classes.titleDescription}>
            <span className={classes.spanCard}>
              Transfer evidence your way:
            </span>{" "}
            Download a kulpa evidence bundle for manual upload, or connect to
            our API for automatic syncing. Either way, it’s simple.
          </Typography>
        </div>
        <Button
          className={classes.button}
          color="secondary"
          variant="contained"
          disableElevation
          component={LinkScroll}
          to={`${urls.getConnected}#demo`}
        >
          Book a demo
        </Button>
      </div>
      <div className={classes.imageWrapper}>
        <img src={mainImage} className={classes.image} alt="Implementation" />
      </div>
    </Section>
  );
};

export default GetConnectedImplementation;
